'use client';

import { isAttuneDesktopShell } from '@/lib/desktop-auth-client';
import { integrationSetupReturnPath } from '@/lib/integrations/setup-return';
import { useIntegrationSetupFlow } from './useIntegrationSetupFlow';
import { IntegrationSetupError } from './IntegrationSetupError';
import { useConnectorDraft } from './useConnectorDraft';

import { Badge, Box, Button, Field, Grid, HStack, Input, Text, VStack } from '@chakra-ui/react';
import {
  AlertCircle,
  Check,
  Database,
  ExternalLink,
  FilePenLine,
  Globe,
  Link2,
  RefreshCw,
  Send,
  ShieldCheck,
  Unlink,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { useAuth } from '@/app/auth-provider';
import {
  IntegrationCard,
  IntegrationNotice,
  IntegrationStatusRow,
} from '@/components/shared/integrations/IntegrationCardShell';
import { InlineSpinner } from '@/components/shared/PageLoader';
import { useProject } from '@/contexts/ProjectContext';

interface WebflowConnection {
  id?: string;
  status?: string | null;
  siteName?: string | null;
  site_name?: string | null;
  grantedScopes?: string[] | null;
  granted_scopes?: string[] | null;
  connectedAt?: string | null;
  connected_at?: string | null;
  lastSyncAt?: string | null;
  last_sync_at?: string | null;
  warnings?: string[] | null;
  authMethod?: 'oauth' | 'site_token';
  auth_method?: 'oauth' | 'site_token';
}

interface WebflowSite {
  id?: string;
  name?: string | null;
  displayName?: string | null;
  display_name?: string | null;
}

interface WebflowCollection {
  id?: string;
  name?: string | null;
  displayName?: string | null;
  display_name?: string | null;
}

interface WebflowPageMapping {
  id?: string;
  status?: string | null;
  mappingConfidence?: string | null;
  mapping_confidence?: string | null;
}

interface SiteTokenDraft {
  projectId: string | null;
  siteId: string;
  siteLabel: string;
  editorToken: string;
}

function emptySiteTokenDraft(projectId: string | null): SiteTokenDraft {
  return { projectId, siteId: '', siteLabel: '', editorToken: '' };
}

interface WebflowDraftJob {
  id?: string;
  status?: string | null;
}

interface WebflowSyncRun {
  id?: string;
  status?: string | null;
  startedAt?: string | null;
  started_at?: string | null;
  completedAt?: string | null;
  completed_at?: string | null;
  error?: string | null;
  errorMessage?: string | null;
  error_message?: string | null;
  errorCode?: string | null;
  error_code?: string | null;
  heartbeatAt?: string | null;
  heartbeat_at?: string | null;
  progress?: {
    phase?: string;
    currentCollection?: string | null;
    collectionsCompleted?: number;
    totalCollections?: number | null;
    itemsProcessed?: number;
  } | null;
}

interface WebflowStatus {
  connected?: boolean;
  connection?: WebflowConnection | null;
  sites?: WebflowSite[] | null;
  collections?: WebflowCollection[] | null;
  pageMappings?: WebflowPageMapping[] | null;
  mappings?: WebflowPageMapping[] | null;
  draftJobs?: WebflowDraftJob[] | null;
  drafts?: WebflowDraftJob[] | null;
  syncRuns?: WebflowSyncRun[] | null;
  lastSync?: WebflowSyncRun | null;
  counts?: {
    sites?: number;
    collections?: number;
    mappings?: number;
    draftJobs?: number;
    publishReady?: number;
  } | null;
  warning?: string | null;
}

type FetchStatusOptions = {
  quiet?: boolean;
};

const EMPTY_STATUS: WebflowStatus = {
  connected: false,
  connection: null,
  sites: [],
  collections: [],
  pageMappings: [],
  draftJobs: [],
  syncRuns: [],
};

function asArray<T>(value: T[] | null | undefined): T[] {
  return Array.isArray(value) ? value : [];
}

function pickString(...values: Array<string | null | undefined>): string | null {
  return values.find(value => typeof value === 'string' && value.trim().length > 0)?.trim() || null;
}

function formatDate(value: string | null | undefined) {
  if (!value) return 'Not synced yet';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Not synced yet';
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

async function readErrorMessage(response: Response, fallback: string) {
  try {
    const payload = await response.json();
    return typeof payload?.error === 'string' ? payload.error : fallback;
  } catch {
    return fallback;
  }
}

function statusPalette(status: string | null | undefined) {
  if (!status) return 'gray';
  const normalized = status.toLowerCase();
  if (
    ['complete', 'completed', 'connected', 'success', 'synced', 'ready', 'published'].includes(
      normalized
    )
  ) {
    return 'green';
  }
  if (['running', 'syncing', 'queued', 'pending'].includes(normalized)) return 'gray';
  if (['failed', 'error', 'revoked', 'disconnected'].includes(normalized)) return 'red';
  return 'orange';
}

export function WebflowIntegration() {
  const { user } = useAuth();
  const { currentProject } = useProject();
  const setup = useIntegrationSetupFlow(currentProject?.id);
  const projectId = currentProject?.id || null;
  const latestProjectIdRef = useRef(projectId);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [siteTokenConnecting, setSiteTokenConnecting] = useState(false);
  const [siteTokenDraft, setSiteTokenDraft] = useState<SiteTokenDraft>(() =>
    emptySiteTokenDraft(projectId)
  );
  const currentSiteTokenDraft =
    siteTokenDraft.projectId === projectId ? siteTokenDraft : emptySiteTokenDraft(projectId);
  const { siteId, siteLabel, editorToken } = currentSiteTokenDraft;
  const [status, setStatus] = useState<WebflowStatus>(EMPTY_STATUS);
  const [loadError, setLoadError] = useState<string | null>(null);

  const clearConnectionDraft = useConnectorDraft(
    user?.uid,
    projectId,
    'webflow',
    { siteId, siteLabel },
    draft => {
      setSiteTokenDraft({
        ...emptySiteTokenDraft(projectId),
        siteId: draft.siteId || '',
        siteLabel: draft.siteLabel || '',
      });
    }
  );

  const fetchStatus = useCallback(
    async (options: FetchStatusOptions = {}) => {
      if (!user || !projectId) {
        setStatus(EMPTY_STATUS);
        setLoading(false);
        return;
      }

      if (!options.quiet) setLoading(true);
      setLoadError(null);
      const requestedProjectId = projectId;
      try {
        const token = await user.getIdToken();
        const params = new URLSearchParams({ projectId: requestedProjectId });
        const response = await fetch(`/api/integrations/webflow/status?${params.toString()}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        if (!response.ok) {
          throw new Error(await readErrorMessage(response, 'Failed to load Webflow status'));
        }
        const payload = (await response.json()) as WebflowStatus;
        if (latestProjectIdRef.current !== requestedProjectId) return;
        setStatus({
          ...EMPTY_STATUS,
          ...payload,
          connected: Boolean(payload.connected || payload.connection),
          connection: payload.connection || null,
        });
        return payload;
      } catch (error) {
        if (latestProjectIdRef.current !== requestedProjectId) return;
        const message = error instanceof Error ? error.message : 'Failed to load Webflow status';
        setLoadError(message);
        console.error('webflow status error', error);
      } finally {
        if (latestProjectIdRef.current === requestedProjectId) {
          setLoading(false);
        }
      }
    },
    [projectId, user]
  );

  useEffect(() => {
    latestProjectIdRef.current = projectId;
    setSiteTokenDraft(current =>
      current.projectId === projectId ? current : emptySiteTokenDraft(projectId)
    );
  }, [projectId]);

  useEffect(() => {
    void fetchStatus();
  }, [fetchStatus]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const success = params.get('webflow_success') || params.get('webflow_connected');
    const warning = params.get('webflow_warning');
    const error = params.get('webflow_error');
    if (!success && !warning && !error) return;

    if (success === 'true' || success === '1') {
      toast.success('Webflow connected.');
    }
    if (warning) {
      toast.warning('Webflow connected with a warning. Review the connection before publishing.');
    }
    if (error) {
      setup.reportError('Webflow connection failed. Please try again.');
    }

    params.delete('webflow_success');
    params.delete('webflow_connected');
    params.delete('webflow_warning');
    params.delete('webflow_error');
    const next = params.toString();
    window.history.replaceState(
      {},
      '',
      next ? `${window.location.pathname}?${next}` : window.location.pathname
    );

    void fetchStatus({ quiet: true }).then(payload => {
      if (
        (success === 'true' || success === '1') &&
        !error &&
        !warning &&
        payload?.connection &&
        (payload.sites?.length || payload.counts?.sites)
      ) {
        clearConnectionDraft();
        setup.onConfigured();
      }
    });
  }, [fetchStatus, setup.onConfigured, clearConnectionDraft]);

  const sites = asArray(status.sites);
  const collections = asArray(status.collections);
  const mappings = asArray(status.pageMappings).length
    ? asArray(status.pageMappings)
    : asArray(status.mappings);
  const drafts = asArray(status.draftJobs).length
    ? asArray(status.draftJobs)
    : asArray(status.drafts);
  const syncRuns = asArray(status.syncRuns);
  const connected = Boolean(status.connected || status.connection);
  const connection = status.connection || null;
  const authMethod = connection?.authMethod || connection?.auth_method || 'oauth';
  const scopes = useMemo(
    () =>
      asArray(connection?.grantedScopes).length
        ? asArray(connection?.grantedScopes)
        : asArray(connection?.granted_scopes),
    [connection]
  );
  const warnings = asArray(connection?.warnings);
  const latestSync = status.lastSync || syncRuns[0] || null;
  const syncStatus = latestSync?.status || (connected ? 'ready' : null);
  const syncActive = ['queued', 'running'].includes((syncStatus || '').toLowerCase());
  const lastSyncedAt = pickString(
    latestSync?.completedAt,
    latestSync?.completed_at,
    latestSync?.startedAt,
    latestSync?.started_at,
    connection?.lastSyncAt,
    connection?.last_sync_at
  );
  const firstSiteName =
    sites.map(site => pickString(site.displayName, site.display_name, site.name)).find(Boolean) ||
    pickString(connection?.siteName, connection?.site_name) ||
    'Webflow site';
  const readyMappings = mappings.filter(mapping =>
    ['ready', 'high', 'confirmed', 'mapped'].includes(
      (
        mapping.mappingConfidence ||
        mapping.mapping_confidence ||
        mapping.status ||
        ''
      ).toLowerCase()
    )
  ).length;
  const siteCount = status.counts?.sites ?? sites.length;
  const collectionCount = status.counts?.collections ?? collections.length;
  const mappingCount = status.counts?.mappings ?? mappings.length;
  const draftCount = status.counts?.draftJobs ?? drafts.length;
  const readyMappingCount = readyMappings;
  const hasWarning =
    Boolean(loadError || status.warning || warnings.length) ||
    ['failed', 'error'].includes((syncStatus || '').toLowerCase());

  useEffect(() => {
    if (!syncActive) return;
    const timer = window.setInterval(() => {
      void fetchStatus({ quiet: true });
    }, 3_000);
    return () => window.clearInterval(timer);
  }, [fetchStatus, syncActive]);

  const handleConnect = async () => {
    if (!user || !projectId) return;
    setup.clearError();
    setConnecting(true);
    try {
      const token = await user.getIdToken();
      const response = await fetch('/api/integrations/webflow/start', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          projectId,
          returnTo: integrationSetupReturnPath('webflow', setup.returnTo, isAttuneDesktopShell()),
        }),
      });
      const payload = await response.json().catch(() => ({}));
      const authorizationUrl = pickString(
        payload.authorizationUrl,
        payload.authorizeUrl,
        payload.initiateUrl,
        payload.redirectUrl,
        payload.url
      );
      if (!response.ok || !authorizationUrl) {
        throw new Error(payload.error || 'Unable to start Webflow OAuth');
      }
      window.location.href = authorizationUrl;
    } catch (error) {
      setup.reportError(error instanceof Error ? error.message : 'Failed to start Webflow OAuth');
      setConnecting(false);
    }
  };

  const handleSiteTokenConnect = async () => {
    if (!user || !projectId) return;
    setup.clearError();
    setSiteTokenConnecting(true);
    try {
      const authToken = await user.getIdToken();
      const response = await fetch('/api/integrations/webflow/site-token', {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ projectId, siteId, siteLabel, token: editorToken }),
      });
      if (!response.ok) {
        throw new Error(await readErrorMessage(response, 'Failed to connect Webflow Site Token'));
      }
      setSiteTokenDraft(current =>
        current.projectId === projectId ? { ...current, editorToken: '' } : current
      );
      toast.success('Webflow CMS connected. Initial sync queued.');
      await fetchStatus({ quiet: true });
      clearConnectionDraft();
      setup.onConfigured();
    } catch (error) {
      setup.reportError(
        error instanceof Error ? error.message : 'Webflow Site Token connection failed'
      );
    } finally {
      setSiteTokenConnecting(false);
    }
  };

  const handleSync = async () => {
    if (!user || !projectId) return;
    setSyncing(true);
    try {
      const token = await user.getIdToken();
      const response = await fetch('/api/integrations/webflow/sync', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ projectId }),
      });
      if (!response.ok) {
        throw new Error(await readErrorMessage(response, 'Failed to start Webflow sync'));
      }
      const payload = (await response.json().catch(() => ({}))) as {
        syncRun?: { status?: string; error?: string };
      };
      toast.success(
        payload.syncRun?.status === 'running' ? 'Webflow sync running.' : 'Webflow sync queued.'
      );
      await fetchStatus({ quiet: true });
    } catch (error) {
      setup.reportError(error instanceof Error ? error.message : 'Webflow sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const handleDisconnect = async () => {
    if (!user || !projectId) return;
    const confirmed = window.confirm(
      'Disconnect Webflow? This removes page mapping access and pauses draft and publish actions.'
    );
    if (!confirmed) return;

    setDisconnecting(true);
    try {
      const token = await user.getIdToken();
      const response = await fetch('/api/integrations/webflow/disconnect', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ projectId }),
      });
      if (!response.ok) {
        throw new Error(await readErrorMessage(response, 'Failed to disconnect Webflow'));
      }
      toast.success('Webflow disconnected.');
      await fetchStatus({ quiet: true });
    } catch (error) {
      setup.reportError(error instanceof Error ? error.message : 'Disconnect failed');
    } finally {
      setDisconnecting(false);
    }
  };

  if (loading) {
    return (
      <IntegrationCard
        icon={<Globe size={18} />}
        title="Webflow Pages & CMS"
        description="Connect Webflow to map Attune pages, prepare copy or CMS drafts, and gate publish actions."
        statusLabel="Loading"
        statusTone="idle"
      >
        <IntegrationSetupError message={setup.error} />
        <IntegrationStatusRow icon={<InlineSpinner size="xs" />} title="Loading status" />
      </IntegrationCard>
    );
  }

  return (
    <IntegrationCard
      icon={<Globe size={18} />}
      title="Webflow Pages & CMS"
      description="Connect Webflow to map Attune pages, prepare copy or CMS drafts, and gate Publish actions."
      statusLabel={connected ? (hasWarning ? 'Review' : 'Connected') : 'Not connected'}
      statusTone={connected ? (hasWarning ? 'warning' : 'connected') : 'idle'}
      action={
        connected ? (
          <HStack gap="2" flexWrap="wrap" justify="flex-end">
            <Button
              size="sm"
              variant="outline"
              onClick={handleSync}
              disabled={syncing || syncActive}
            >
              {syncing || syncActive ? <InlineSpinner size="xs" /> : <RefreshCw size={14} />}
              <Text ml="1">
                {syncing || syncActive
                  ? syncStatus === 'queued'
                    ? 'Queued'
                    : 'Syncing...'
                  : syncStatus === 'failed'
                    ? 'Retry sync'
                    : 'Sync'}
              </Text>
            </Button>
            <Button
              size="sm"
              variant="ghost"
              color="ui.text.tertiary"
              onClick={handleDisconnect}
              disabled={disconnecting}
              _hover={{ color: 'status.danger.fg', bg: 'ui.surface.card' }}
            >
              {disconnecting ? <InlineSpinner size="xs" /> : <Unlink size={14} />}
              <Text ml="1">Disconnect</Text>
            </Button>
          </HStack>
        ) : (
          <Button
            size="sm"
            bg="accent.solid"
            color="accent.contrast"
            borderRadius="glassButton"
            onClick={handleConnect}
            disabled={connecting || !projectId}
            _hover={{ bg: 'accent.emphasized' }}
          >
            {connecting ? <InlineSpinner size="xs" /> : <ExternalLink size={14} />}
            <Text ml="2">{connecting ? 'Connecting...' : 'Connect OAuth'}</Text>
          </Button>
        )
      }
      meta={
        connected ? (
          <HStack gap="2" flexWrap="wrap">
            <Badge bg="accent.subtle" color="accent.fg" variant="subtle" size="sm">
              {authMethod === 'site_token' ? 'CMS Site Token' : 'OAuth'}
            </Badge>
            <Badge colorPalette={statusPalette(syncStatus)} variant="subtle" size="sm">
              {syncStatus || 'ready'}
            </Badge>
          </HStack>
        ) : null
      }
    >
      <IntegrationSetupError message={setup.error} />
      {connected && siteCount === 0 && authMethod === 'oauth' && (
        <IntegrationNotice
          tone="warning"
          action={
            <Button size="sm" onClick={handleConnect} loading={connecting}>
              Choose sites
            </Button>
          }
        >
          Authorize at least one Webflow site to finish connecting this workspace.
        </IntegrationNotice>
      )}
      {loadError && (
        <IntegrationNotice tone="danger" icon={<AlertCircle size={16} />}>
          {loadError}
        </IntegrationNotice>
      )}
      {status.warning && (
        <IntegrationNotice tone="warning" icon={<AlertCircle size={16} />}>
          {status.warning}
        </IntegrationNotice>
      )}
      {warnings.length > 0 && warnings.length <= 3 && (
        <IntegrationNotice tone="warning" icon={<AlertCircle size={16} />}>
          {warnings.join(' ')}
        </IntegrationNotice>
      )}
      {warnings.length > 3 && (
        <IntegrationNotice tone="warning" icon={<AlertCircle size={16} />}>
          <Text>{warnings.length} Webflow collections need review.</Text>
          <Box as="details" mt="2">
            <Box as="summary" cursor="pointer" fontWeight="600">
              Review details
            </Box>
            <VStack align="stretch" gap="1" mt="2">
              {warnings.map((warning, index) => (
                <Text key={`${index}-${warning}`} fontSize="xs">
                  {warning}
                </Text>
              ))}
            </VStack>
          </Box>
        </IntegrationNotice>
      )}

      {connected ? (
        <>
          <Grid templateColumns={{ base: '1fr', md: 'repeat(4, minmax(0, 1fr))' }} gap="3" py="3">
            <WebflowMetric label="Sites" value={siteCount} />
            <WebflowMetric label="Collections" value={collectionCount} />
            <WebflowMetric label="Mappings" value={mappingCount} />
            <WebflowMetric label="Drafts" value={draftCount} />
          </Grid>
          <IntegrationStatusRow
            icon={<ShieldCheck size={16} />}
            title={authMethod === 'site_token' ? 'CMS Site Token' : 'OAuth'}
            description={
              scopes.length ? scopes.join(', ') : 'sites:read, pages:read, cms:read, cms:write'
            }
          />
          <IntegrationStatusRow
            icon={<Database size={16} />}
            title={firstSiteName}
            description={
              authMethod === 'site_token'
                ? `${collectionCount.toLocaleString()} staged CMS collections synced for headless delivery.`
                : `${collectionCount.toLocaleString()} CMS collections synced plus static page inventory for mapping.`
            }
          />
          <IntegrationStatusRow
            icon={<Link2 size={16} />}
            title="Page mappings"
            description={`${mappingCount.toLocaleString()} Attune pages mapped to Webflow CMS or static pages.`}
            action={
              <Badge colorPalette={readyMappingCount > 0 ? 'green' : 'gray'} variant="subtle">
                {readyMappingCount.toLocaleString()} ready
              </Badge>
            }
          />
          <IntegrationStatusRow
            icon={<FilePenLine size={16} />}
            title="Copy and CMS drafts"
            description="Prepare reviewable jobs for mapped page copy or CMS recommendations before publishing."
          />
          <IntegrationStatusRow
            icon={<Send size={16} />}
            title="Publish"
            description="Publishing remains gated by prepare/confirm controls for sensitive write actions."
          />
          <IntegrationStatusRow
            icon={syncActive ? <InlineSpinner size="xs" /> : <Check size={16} />}
            title={syncActive ? 'Sync progress' : 'Last sync'}
            description={
              syncActive
                ? [
                    latestSync?.progress?.currentCollection || latestSync?.progress?.phase,
                    latestSync?.progress?.collectionsCompleted !== undefined
                      ? `${latestSync.progress.collectionsCompleted}/${latestSync.progress.totalCollections ?? '?'} collections`
                      : null,
                    latestSync?.progress?.itemsProcessed !== undefined
                      ? `${latestSync.progress.itemsProcessed.toLocaleString()} items`
                      : null,
                  ]
                    .filter(Boolean)
                    .join(' · ') || 'Starting worker…'
                : formatDate(lastSyncedAt)
            }
            action={
              latestSync?.error || latestSync?.errorMessage || latestSync?.error_message ? (
                <Badge colorPalette="red" variant="subtle">
                  Failed
                </Badge>
              ) : null
            }
          />
        </>
      ) : (
        <>
          <IntegrationStatusRow
            icon={<ShieldCheck size={16} />}
            title="OAuth"
            description="Authorize Webflow with site, page, CMS read, CMS write, and page write scopes."
          />
          <IntegrationStatusRow
            icon={<Link2 size={16} />}
            title="Mapping"
            description="Sync sites, collections, fields, CMS items, and static pages before drafting."
          />
          <IntegrationStatusRow
            icon={<FilePenLine size={16} />}
            title="Copy, Drafts, and Publish"
            description="Mapped pages can stage copy or CMS changes first; publish actions stay confirm-gated."
          />
          <Box
            mt="3"
            p="4"
            borderWidth="1px"
            borderColor="ui.border.subtle"
            borderRadius="glass"
            bg="ui.surface.selected"
          >
            <VStack align="stretch" gap="3">
              <Box>
                <Text fontSize="sm" fontWeight="700">
                  Connect headless Webflow CMS
                </Text>
                <Text fontSize="xs" color="ui.text.tertiary">
                  Create a site-specific token with Sites read and CMS read scopes for headless
                  content sync. Approved CMS writes and publishing require the OAuth connection
                  above.
                </Text>
              </Box>
              <Field.Root required>
                <Field.Label>Webflow site ID</Field.Label>
                <Input
                  value={siteId}
                  onChange={event =>
                    setSiteTokenDraft({ ...currentSiteTokenDraft, siteId: event.target.value })
                  }
                />
              </Field.Root>
              <Field.Root>
                <Field.Label>Site label</Field.Label>
                <Input
                  value={siteLabel}
                  onChange={event =>
                    setSiteTokenDraft({ ...currentSiteTokenDraft, siteLabel: event.target.value })
                  }
                  placeholder="Marketing site"
                />
              </Field.Root>
              <Field.Root required>
                <Field.Label>CMS reader token</Field.Label>
                <Input
                  type="password"
                  autoComplete="new-password"
                  value={editorToken}
                  onChange={event =>
                    setSiteTokenDraft({ ...currentSiteTokenDraft, editorToken: event.target.value })
                  }
                />
                <Field.HelperText>
                  Webflow Site Tokens cannot be scope-introspected, so Attune verifies both site
                  metadata and CMS collection access before saving.
                </Field.HelperText>
              </Field.Root>
              <Button
                size="sm"
                onClick={() => void handleSiteTokenConnect()}
                loading={siteTokenConnecting}
                disabled={!siteId.trim() || !editorToken.trim() || !projectId}
              >
                Connect CMS token
              </Button>
            </VStack>
          </Box>
        </>
      )}
    </IntegrationCard>
  );
}

function WebflowMetric({ label, value }: { label: string; value: number }) {
  return (
    <Box
      borderWidth="1px"
      borderStyle="solid"
      borderColor="ui.border.subtle"
      borderRadius="glass"
      bg="ui.surface.selected"
      px="3"
      py="2"
      minW={0}
    >
      <Text fontSize="xs" color="ui.text.tertiary" fontWeight="700" textTransform="uppercase">
        {label}
      </Text>
      <Text fontSize="lg" color="ui.text.primary" fontWeight="700">
        {value.toLocaleString()}
      </Text>
    </Box>
  );
}
